import React, { useState, useContext } from 'react';
import { signOut } from 'firebase/auth';
import { Timestamp } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { auth } from '../../services/firebase/firebaseConfig';
import { AuthContext } from '../Providers/AuthContext';
import { AnonymousAccountManager } from '../../services/auth/AnonymousAccountManager';
import { TimeControlType } from '../../services/MMR/types';
import { MMRService } from '../../services/MMR/MMRService';
import TimeControlBadge from '../TimeControlBadge/TimeControlBadge';
import styles from './UserProfileModal.module.css';

interface UserData {
  uid: string;
  email: string;
  username: string;
  elo: number;
  gamesPlayed: number;
  createdAt: Timestamp;
  lastUsernameChange?: Timestamp;
  usernameChangeCount: number;
  zodiacResult?: string;
}

interface UserProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  userData: UserData | null;
  changeUsername?: (newUsername: string) => Promise<{ success: boolean; error?: string }>;
  canChangeUsername?: () => { canChange: boolean; reason?: string };
}

function UserProfileModal({ isOpen, onClose, userData, changeUsername, canChangeUsername }: UserProfileModalProps) {
  const navigate = useNavigate();
  const { userProfile, accountType, user, isPremium } = useContext(AuthContext);
  const [isEditingUsername, setIsEditingUsername] = useState(false);
  const [newUsername, setNewUsername] = useState('');
  const [usernameError, setUsernameError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPromoteModal, setShowPromoteModal] = useState(false);

  if (!isOpen) return null;

  // Use userProfile from AuthContext if userData not provided (temp account case)
  const profileData = userData || (userProfile ? {
    uid: user?.uid || 'temp',
    email: user?.email || '',
    username: userProfile.username,
    elo: MMRService.getHighestRating(userProfile).rating,
    gamesPlayed: userProfile.mmr.totalGames,
    // Ensure createdAt is properly formatted - handle both Date objects and Timestamps
    createdAt: {
      toDate: () => {
        const date = userProfile.mmr.createdAt;
        // If it's already a Date object, return it
        if (date instanceof Date) return date;
        // If it's a Firestore Timestamp with toDate method, call it
        if (date && typeof date === 'object' && 'toDate' in date) return (date as any).toDate();
        // If it's a string or number, convert it
        if (typeof date === 'string' || typeof date === 'number') return new Date(date);
        // Fallback to now
        return new Date();
      }
    } as Timestamp,
    usernameChangeCount: 0,
    zodiacResult: userProfile.zodiacResult
  } : null);

  if (!profileData) return null;

  const handleSignOut = async () => {
    try {
      await signOut(auth);
      onClose();
    } catch (error) {
    }
  };

  const handleUsernameEdit = () => {
    // For temp accounts, use AnonymousAccountManager
    if (accountType === 'temp') {
      const canChange = AnonymousAccountManager.canChangeTempUsername();
      if (!canChange.canChange) {
        setUsernameError(canChange.reason || 'Cannot change username');
        return;
      }
    } else if (canChangeUsername) {
      const canChange = canChangeUsername();
      if (!canChange.canChange) {
        setUsernameError(canChange.reason || 'Cannot change username');
        return;
      }
    } else {
      setUsernameError('Username changing not available');
      return;
    }
    setIsEditingUsername(true);
    setNewUsername(profileData.username);
    setUsernameError('');
  };

  const handleUsernameSubmit = async () => {
    if (!newUsername.trim() || newUsername === profileData.username) {
      setIsEditingUsername(false);
      return;
    }

    setLoading(true);
    setUsernameError('');

    let result;
    if (accountType === 'temp') {
      result = await AnonymousAccountManager.changeTempAccountUsername(newUsername);
    } else if (changeUsername) {
      result = await changeUsername(newUsername);
    } else {
      result = { success: false, error: 'Username changing not available' };
    }

    if (result.success) {
      setIsEditingUsername(false);
      setNewUsername('');
      // Force a small delay to allow state to update
      setTimeout(() => {
        window.location.reload();
      }, 500);
    } else {
      setUsernameError(result.error || 'Failed to change username');
    }

    setLoading(false);
  };

  const handleUsernameCancel = () => {
    setIsEditingUsername(false);
    setNewUsername('');
    setUsernameError('');
  };

  const canChange = accountType === 'temp'
    ? AnonymousAccountManager.canChangeTempUsername()
    : canChangeUsername ? canChangeUsername() : { canChange: false, reason: 'Not available' };

  const getPieceImage = (zodiacResult: string) => {
    if (!zodiacResult) return '';
    const [variant, ...nameParts] = zodiacResult.split(' ');
    const pieceName = nameParts.join(' ');
    return `/Icons/${pieceName}-${variant.toLowerCase()}.png`;
  };

  const canChangeAvatar = () => {
    // Temp accounts can always change
    if (accountType === 'temp') return { canChange: true };

    // Owner account bypass for testing
    if (userProfile?.username === 'Dscvr') return { canChange: true };

    // Check 90-day cooldown for real accounts
    if (userProfile?.lastAvatarChange) {
      const daysSinceLastChange = Math.floor(
        (Date.now() - userProfile.lastAvatarChange.getTime()) / (1000 * 60 * 60 * 24)
      );
      if (daysSinceLastChange < 90) {
        const daysRemaining = 90 - daysSinceLastChange;
        return { canChange: false, reason: `You can change your avatar in ${daysRemaining} days` };
      }
    }

    return { canChange: true };
  };

  const handleAvatarClick = () => {
    const avatarCheck = canChangeAvatar();
    if (!avatarCheck.canChange) {
      setUsernameError(avatarCheck.reason || 'Cannot change avatar');
      return;
    }
    navigate('/whats-my-zodiac');
    onClose();
  };

  const handlePromoteAccount = () => {
    setShowPromoteModal(true);
  };

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <button className={styles.closeButton} onClick={onClose}>
          ×
        </button>

        <h2 className={styles.title}>
          {accountType === 'temp' ? 'Temporary Account' : 'Player Profile'}
          {accountType === 'temp' && (
            <span className={styles.tempBadge}>TEMP</span>
          )}
          {isPremium && accountType === 'real' && (
            <span className={styles.premiumBadge}>PREMIUM</span>
          )}
        </h2>

        <div className={styles.profileSection}>
          <div className={styles.avatarContainer}>
            <div
              className={`${styles.avatar} ${canChangeAvatar().canChange ? styles.avatarClickable : styles.avatarDisabled}`}
              onClick={handleAvatarClick}
              title={
                !canChangeAvatar().canChange
                  ? canChangeAvatar().reason
                  : profileData.zodiacResult
                    ? `Your zodiac: ${profileData.zodiacResult} - Click to retake quiz`
                    : 'Take the zodiac quiz to get your spirit animal'
              }
            >
              {profileData.zodiacResult ? (
                <img
                  src={getPieceImage(profileData.zodiacResult!)}
                  alt={profileData.zodiacResult}
                  className={styles.zodiacIcon}
                />
              ) : (
                profileData.username.charAt(0).toUpperCase()
              )}
            </div>
          </div>

          <div className={styles.statsContainer}>
            <div className={styles.statItem}>
              <span className={styles.statLabel}>Username:</span>
              {isEditingUsername ? (
                <div className={styles.usernameEditContainer}>
                  <input
                    type="text"
                    value={newUsername}
                    onChange={(e) => setNewUsername(e.target.value)}
                    className={styles.usernameInput}
                    maxLength={12}
                    disabled={loading}
                  />
                  <div className={styles.usernameEditButtons}>
                    <button
                      onClick={handleUsernameSubmit}
                      className={styles.usernameSubmitButton}
                      disabled={loading}
                    >
                      {loading ? '...' : '✓'}
                    </button>
                    <button
                      onClick={handleUsernameCancel}
                      className={styles.usernameCancelButton}
                      disabled={loading}
                    >
                      ✕
                    </button>
                  </div>
                </div>
              ) : (
                <div className={styles.usernameDisplayContainer}>
                  <span className={styles.statValue}>{profileData.username}</span>
                  <button
                    onClick={handleUsernameEdit}
                    className={styles.usernameEditButton}
                    title={canChange.canChange ? 'Edit username' : canChange.reason}
                    disabled={!canChange.canChange}
                  >
                    ✏️
                  </button>
                </div>
              )}
            </div>

            {usernameError && (
              <div className={styles.usernameError}>
                {usernameError}
              </div>
            )}

            <div className={styles.statItem}>
              <span className={styles.statLabel}>Rating:</span>
              <span className={styles.statValue}>
                {accountType === 'temp' && userProfile && !MMRService.getHighestRating(userProfile).isPlaced
                  ? `Placement ${userProfile.mmr.placementGames}/10`
                  : `${profileData.elo} ELO`
                }
              </span>
            </div>

            {/* Show peak ELO for placed players */}
            {userProfile && userProfile.mmr.isPlaced && (
              <div className={styles.statItem}>
                <span className={styles.statLabel}>Peak ELO:</span>
                <span className={styles.statValue}>
                  {Math.max(
                    userProfile.mmr.peak || 0,
                    ...(userProfile.mmr.timeControlRatings
                      ? (['sprint', 'standard', 'marathon'] as const).map(
                          tc => userProfile.mmr.timeControlRatings?.[tc]?.peak || 0
                        )
                      : []),
                    profileData.elo
                  )} ELO
                </span>
              </div>
            )}

            {/* Show time control ratings if available */}
            {userProfile && userProfile.mmr.timeControlRatings && (
              <div className={styles.ratingsSection}>
                <h3 className={styles.ratingsTitle}>Ratings by Time Control</h3>
                <div className={styles.ratingsGrid}>
                  {(['sprint', 'standard', 'marathon'] as TimeControlType[]).map(tc => {
                    const tcRating = userProfile.mmr.timeControlRatings?.[tc];
                    return (
                      <TimeControlBadge
                        key={tc}
                        timeControl={tc}
                        rating={tcRating ? (tcRating.isPlaced ? tcRating.rating : tcRating.provisionalRating) : 1300}
                        games={tcRating?.games || 0}
                        isPlaced={tcRating?.isPlaced || false}
                        placementGames={tcRating?.placementGames || 0}
                      />
                    );
                  })}
                </div>
                <button
                  className={styles.leaderboardLink}
                  onClick={() => {
                    navigate('/leaderboard');
                    onClose();
                  }}
                >
                  View Leaderboard
                </button>
              </div>
            )}

            <div className={styles.statItem}>
              <span className={styles.statLabel}>Games Played:</span>
              <span className={styles.statValue}>{profileData.gamesPlayed}</span>
            </div>

            <div className={styles.statItem}>
              <span className={styles.statLabel}>{accountType === 'temp' ? 'Created' : 'Member Since'}</span>
              <span className={styles.statValue}>
                {(() => {
                  try {
                    // Try multiple date sources in order of preference
                    let dateToFormat: Date | null = null;

                    // 1. Try createdAt from profileData (Firestore Timestamp)
                    if (profileData.createdAt && typeof profileData.createdAt.toDate === 'function') {
                      const converted = profileData.createdAt.toDate();
                      if (!isNaN(converted.getTime())) {
                        dateToFormat = converted;
                      }
                    }

                    // 2. Try mmr.createdAt from userProfile
                    if (!dateToFormat && userProfile?.mmr?.createdAt) {
                      const mmrDate = userProfile.mmr.createdAt instanceof Date
                        ? userProfile.mmr.createdAt
                        : new Date(userProfile.mmr.createdAt);
                      if (!isNaN(mmrDate.getTime())) {
                        dateToFormat = mmrDate;
                      }
                    }

                    // 3. Try lastActive as fallback
                    if (!dateToFormat && userProfile?.lastActive) {
                      const lastActiveDate = userProfile.lastActive instanceof Date
                        ? userProfile.lastActive
                        : new Date(userProfile.lastActive);
                      if (!isNaN(lastActiveDate.getTime())) {
                        dateToFormat = lastActiveDate;
                      }
                    }

                    // 4. Use current date as last resort (shouldn't happen)
                    if (!dateToFormat) {
                      dateToFormat = new Date();
                    }

                    return dateToFormat.toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long'
                    });
                  } catch (error) {
                    return 'Recently';
                  }
                })()}
              </span>
            </div>

            {isPremium && accountType === 'real' && (
              <div className={styles.premiumAccountInfo}>
                <div className={styles.premiumStatus}>
                  ✨ Premium Account Active
                </div>
                <div className={styles.premiumBenefits}>
                  <p className={styles.premiumLabel}>Premium Benefits:</p>
                  <ul className={styles.premiumList}>
                    <li>📜 Full game history access</li>
                    <li>📊 Advanced analytics</li>
                    <li>🎓 Exclusive courses</li>
                  </ul>
                </div>
              </div>
            )}

            {accountType === 'temp' && (
              <div className={styles.tempAccountInfo}>
                <div className={styles.tempAccountWarning}>
                  ⚠️ This is a temporary account. Progress may be lost if you clear your browser data.
                </div>
                <button
                  onClick={handlePromoteAccount}
                  className={styles.promoteAccountButton}
                >
                  🔒 Create Real Account
                </button>
                <div className={styles.signInPrompt}>
                  Already have an account?{' '}
                  <button
                    onClick={() => {
                      onClose();
                      document.dispatchEvent(new CustomEvent('openAuthModal', { detail: { mode: 'signIn' } }));
                    }}
                    className={styles.signInLink}
                  >
                    Sign in here
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className={styles.buttonContainer}>
          <button
            onClick={handleSignOut}
            className={styles.signOutButton}
          >
            Sign Out
          </button>
        </div>

        {/* Account Promotion Modal */}
        {showPromoteModal && (
          <div className={styles.promoteModalOverlay} onClick={() => setShowPromoteModal(false)}>
            <div className={styles.promoteModalContent} onClick={(e) => e.stopPropagation()}>
              <button className={styles.closeButton} onClick={() => setShowPromoteModal(false)}>
                ×
              </button>
              <h3 className={styles.promoteTitle}>Create Real Account</h3>
              <p className={styles.promoteDescription}>
                Secure your progress by creating a real account. Your current stats and progress will be preserved.
              </p>
              <div className={styles.currentProgress}>
                <h4>Your Current Progress:</h4>
                <ul>
                  <li>Username: {profileData.username}</li>
                  <li>Games Played: {profileData.gamesPlayed}</li>
                  <li>Rating: {profileData.elo}</li>
                  {profileData.zodiacResult && <li>Zodiac: {profileData.zodiacResult}</li>}
                </ul>
              </div>
              <div className={styles.promoteActions}>
                <button
                  className={styles.promoteConfirmButton}
                  onClick={() => {
                    setShowPromoteModal(false);
                    onClose();
                    // This will trigger the AuthModal to open in SIGN UP mode
                    document.dispatchEvent(new CustomEvent('openAuthModal', { detail: { mode: 'signUp' } }));
                  }}
                >
                  🔐 Create Account
                </button>
                <button
                  className={styles.promoteCancelButton}
                  onClick={() => setShowPromoteModal(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default UserProfileModal;